import {
  Barcode,
  BarcodeGenerationType,
  BarcodeStatus,
} from '@buncha/driver/types/printScanBarcode';
import {Box, HBox} from '@buncha/components/core/view';
import {
  FlatBarcode,
  ScannerMode,
} from '@buncha/components/composites/Scanner/type';
import React, {useCallback, useEffect, useState} from 'react';
import {
  ScanBarcodeAction,
  useScanBarcodeContext,
} from '@buncha/driver/hooks/ScanBarcode';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import CheckIcon from '@buncha/components/core/icon/CheckIcon';
import CustomFlatList from '@buncha/components/core/list/CustomFlatList';
import {OrderInfo} from '@buncha/config/graphQL/type';
import {PSmallText} from '@buncha/components/core/text';
import {ScanBarcodeParams} from '@buncha/driver/types/scanBarcode';
import Scanner from '@buncha/components/composites/Scanner';
import {StyleSheet} from 'react-native';
import {navigation} from '@buncha/config/navigation';
import {useRoute} from '@react-navigation/native';
import {useUpdateBarcodeStatus} from '@buncha/driver/hooks/printScanBarcodes';

interface Props {
  orderIds: number[];
  orderInfo: OrderInfo[];
}

const ScannerComponent = (props: Props) => {
  const route = useRoute();
  const params: ScanBarcodeParams | undefined = route?.params;
  const barcodeType = params?.barcodeType ?? BarcodeGenerationType.system;
  const runId = params?.runId;
  const [isActive, setIsActive] = useState(false);
  const [barcodeList, setBarcodeList] = useState(params?.barcodeList ?? []);
  const {dispatch} = useScanBarcodeContext();
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const {updateBarcodeStatus, barcodes} = useUpdateBarcodeStatus(barcodeType);

  const onScannerActiveHandler = useCallback((activeState: boolean) => {
    setIsActive(activeState);
  }, []);

  const navigateHandler = useCallback(() => {
    navigation.goBack();
  }, []);

  const scanCallbackHandler = useCallback(
    (scanned: number) => {
      const total = barcodeList?.length || 0;
      if (total === scanned) {
        onScannerActiveHandler(false);
        navigateHandler();
      } else {
        const dataToUpdate = {
          total: total,
          scanned: scanned,
        };
        if (dispatch)
          dispatch(ScanBarcodeAction.setBarcodesCount(dataToUpdate));
        onScannerActiveHandler(true);
      }
    },
    [barcodeList, dispatch, navigateHandler, onScannerActiveHandler],
  );

  useEffect(() => {
    if (barcodes.length) setBarcodeList(barcodes);
  }, [barcodes]);

  const onScanHandler = useCallback(
    async (barcode: FlatBarcode) => {
      const upc = barcode.data ?? '';
      try {
        await updateBarcodeStatus(
          upc,
          BarcodeStatus.scanned,
          props?.orderIds,
          scanCallbackHandler,
          runId || 0,
          props?.orderInfo,
        );
      } catch (error) {
        onScannerActiveHandler(true);
      }
    },
    [
      onScannerActiveHandler,
      props?.orderIds,
      props?.orderInfo,
      runId,
      scanCallbackHandler,
      updateBarcodeStatus,
    ],
  );

  const renderItem = useCallback(
    (item: {item: Barcode}) => {
      const iconColor =
        item?.item?.status === BarcodeStatus.generated
          ? theme?.icon?.gray?.[200]
          : theme?.icon?.green?.[100];

      return (
        <HBox style={styles.renderItemBox}>
          <CheckIcon
            height={15}
            width={15}
            fill={iconColor}
            viewBox="1 -5 40 50"
          />
          <PSmallText style={{fontFamily: muli?.[700]?.normal}}>
            {item?.item?.code}
          </PSmallText>
        </HBox>
      );
    },
    [muli, theme?.icon?.gray, theme?.icon?.green],
  );

  return (
    <Box style={styles.container}>
      <Box style={styles.flatListBox}>
        <CustomFlatList
          persistentScrollbar
          scrollEnabled
          numColumns={3}
          data={barcodeList}
          renderItem={renderItem}
        />
      </Box>

      <Scanner
        mode={ScannerMode.singleScan}
        onScan={onScanHandler}
        scannerEnabled={isActive}
        setScannerEnabled={setIsActive}
      />
    </Box>
  );
};

export default React.memo(ScannerComponent);

const styles = StyleSheet.create({
  container: {flexGrow: 1, marginTop: 10},
  flatListBox: {
    flex: 0,
    paddingLeft: 20,
    paddingBottom: 10,
    maxHeight: 60,
  },
  renderItemBox: {
    minWidth: '33%',
    justifyContent: 'flex-start',
    gap: 1,
  },
});
